import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { IThemeContext, useCredentialData, useThemeContext } from '../../contexts';
import CredentialList from './CredentialList';
import { FullScreenSpinner } from '../../components/molecules/Spinner';
import EmptyState from './card/components/EmptyState';
import {
  getResponsivePadding,
  getResponsiveContainerWidth,
  getFluidFontSize
} from '../../helpers/utils';

interface CredentialHomeProps {
  showWelcome: boolean;
}

/**
 * @description Credential home component
 * @param {CredentialHomeProps} { showWelcome }
 * @return {React.FC}
 */
const CredentialHome: React.FC<CredentialHomeProps> = ({ showWelcome }) => {
  const { theme } = useThemeContext();
  const [isFirstView, setIsFirstView] = useState<boolean>(true);
  const { credentials, loading } = useCredentialData();
  const styles = createCredentialListStyles(theme);

  React.useEffect(() => {
    const hasViewed = localStorage.getItem('hasViewed');
    if (hasViewed) {
      setIsFirstView(false);
    } else {
      localStorage.setItem('hasViewed', 'true');
    }
  }, []);

  if (loading && credentials.length < 1) {
    return <FullScreenSpinner text="Loading your wallet..." />;
  }

  return (
    <View style={styles.container}>
      {credentials.length === 0 ? (
        <EmptyState showWelcome={showWelcome} isFirstView={isFirstView} />
      ) : (
        <CredentialList credentials={credentials} />
      )}
    </View>
  );
};

export default CredentialHome;

const createCredentialListStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    container: {
      width: '100%',
      minHeight: '100%',
      maxWidth: getResponsiveContainerWidth()
    },
    listContainer: {
      gap: 0,
      padding: getResponsivePadding(12, 16, 20)
    },
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      width: '100%',
      gap: getResponsivePadding(16, 20, 24),
      height: '100%',
      padding: getResponsivePadding(16, 20, 24),
      margin: 'auto'
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: getResponsivePadding(8, 10, 12)
    },
    emptyListMessageTitleStyle: {
      fontSize: getFluidFontSize(22, 24, 26, 20, 30),
      fontWeight: '700',
      textAlign: 'center'
    },
    emptyListMessageSubtitleStyle: {
      fontSize: getFluidFontSize(16, 17, 18, 14, 20),
      color: theme.textSecondary,
      textAlign: 'center',
      paddingHorizontal: getResponsivePadding(8, 12, 16)
    },
    onboardButton: {
      paddingVertical: getResponsivePadding(12, 14, 16),
      paddingHorizontal: getResponsivePadding(20, 24, 28),
      backgroundColor: theme.primary,
      borderRadius: 6,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)',
      width: '100%',
      maxWidth: 300
    },
    onboardButtonText: {
      textAlign: 'center',
      color: 'inherit',
      fontSize: getFluidFontSize(14, 15, 16, 12, 18),
      fontWeight: '600'
    },
    credentialCard: {
      width: '100%',
      boxShadow: '0 -2px 0 #fff',
      borderRadius: 9
    }
  });
